import React, { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { CommonTextField } from "../../components/widgets/common_textField";
import MarketplaceService from "../../service/marketplace.service";
import { toastError, toastSuccess } from "../../common/toastNotification";
import CommonButton from "../../components/widgets/common_button";
import { Trash2, SquarePen, Copy, Check, Gift, Store, ExternalLink } from "lucide-react";
import CommonModal from "../../components/widgets/CommonModal";
import Delete from "./Delete";

const WEB_BASE_URL = "https://watchcert.io";
const LOCAL_TEST_URL = "http://localhost:8080/?marketplace=test_2";

const formatSlug = (text) => {
  return (text || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
};

const Marketplace = () => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    hasFreeCredit: true,
    totalCredits: 50,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchMarketplaces = async () => {
    try {
      const response = await MarketplaceService.getMarketplaces();
      const list =
        response?.data?.apiresponse?.data ||
        response?.data?.data ||
        response?.data ||
        [];
      setData(Array.isArray(list) ? list : []);
    } catch (error) {
      toastError(error?.response?.data?.message || error?.message || "Failed to load marketplaces");
      setData([]);
    }
  };

  useEffect(() => {
    try {
      localStorage.removeItem("watchcert_admin_marketplaces");
    } catch (e) {
      // ignore
    }
    fetchMarketplaces();
  }, []);

  const handleNameChange = (e) => {
    const name = e.target.value;
    const autoSlug = formatSlug(name);
    setFormData((prev) => ({
      ...prev,
      name,
      slug: autoSlug,
    }));
  };

  const handleSave = async () => {
    try {
      const trimmedName = formData.name.trim();
      const cleanSlug = (formData.slug || formatSlug(trimmedName)).replace(/^_+|_+$/g, "");

      if (!trimmedName || !cleanSlug) {
        toastError("Please enter a valid marketplace name");
        return;
      }

      setLoading(true);

      const creditPool = formData.hasFreeCredit ? Math.max(0, parseInt(formData.totalCredits, 10) || 0) : 0;
      const payload = {
        name: trimmedName,
        slug: cleanSlug,
        hasFreeCredit: Boolean(formData.hasFreeCredit),
        freeCreditAmount: formData.hasFreeCredit ? 1 : 0,
        totalCredits: creditPool,
        credits: creditPool,
      };

      if (isEditing) {
        const response = await MarketplaceService.updateMarketplace(editId, payload);
        if (response?.data?.status === "SUCCESS" || response?.status === 200 || response?.data?.apiresponse?.status) {
          toastSuccess("Marketplace updated successfully!");
        }
      } else {
        const response = await MarketplaceService.addMarketplace(payload);
        if (response?.data?.status === "SUCCESS" || response?.status === 200 || response?.data?.apiresponse?.status) {
          toastSuccess("Marketplace created successfully!");
        }
      }

      await fetchMarketplaces();
      setShowModal(false);
      setFormData({ name: "", slug: "", hasFreeCredit: true, totalCredits: 50 });
      setIsEditing(false);
      setEditId(null);
    } catch (error) {
      toastError(error?.response?.data?.message || error?.message || "Failed to save marketplace");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    const existingCredits =
      item.totalCredits != null
        ? item.totalCredits
        : item.credits != null
        ? item.credits
        : 50;

    setFormData({
      name: item.name || "",
      slug: item.slug || formatSlug(item.name || ""),
      hasFreeCredit: item.hasFreeCredit !== false,
      totalCredits: existingCredits,
    });
    setEditId(item.id);
    setIsEditing(true);
    setShowModal(true);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    const idToDelete = deleteId;
    setShowDeleteDialog(false);
    setDeleteId(null);

    try {
      const response = await MarketplaceService.deleteMarketplace(idToDelete);
      if (response?.status === 200 || response?.data?.status === "SUCCESS" || response?.data?.apiresponse?.status) {
        toastSuccess("Marketplace deleted successfully!");
      }
      await fetchMarketplaces();
    } catch (error) {
      toastError(error?.response?.data?.message || error?.message || "Failed to delete marketplace");
    }
  };

  const copyToClipboard = (slug, id) => {
    const url = `${WEB_BASE_URL}/?marketplace=${slug}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopiedId(id);
    toastSuccess("Partner URL copied to clipboard!");
    setTimeout(() => {
      setCopiedId(null);
    }, 2500);
  };

  return (
    <Card className="p-6 md:p-10 grid gap-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Store className="h-6 w-6 text-primary" />
            <h3 className="text-xl tab:text-2xl font-bold">Marketplaces</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Create and track partner marketplace links with optional 1 Free Credit for first-time visitors.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
          {/* Button to test http://localhost:8080/?marketplace=xyz */}
          {/* <CommonButton
            variant="outline"
            className="w-full sm:w-auto px-4 border-amber-500/40 text-amber-700 bg-amber-50/50 hover:bg-amber-100/70 dark:text-amber-300 dark:bg-amber-950/20"
            onClick={() => window.open(LOCAL_TEST_URL, "_blank")}
            title="Open http://localhost:8080/?marketplace=xyz in new tab"
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            <span>Test URL (xyz)</span>
          </CommonButton> */}

          <CommonButton
            className="w-full sm:w-auto px-5"
            onClick={() => {
              setFormData({ name: "", slug: "", hasFreeCredit: true, totalCredits: 50 });
              setIsEditing(false);
              setEditId(null);
              setShowModal(true);
            }}
          >
            <span className="text-base font-medium">+ Add Marketplace</span>
          </CommonButton>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden shadow-sm">
        <Table className="whitespace-nowrap">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-12 text-center">No</TableHead>
              <TableHead>Marketplace Name</TableHead>
              <TableHead>Parameter / Slug</TableHead>
              <TableHead>Generated Partner URL</TableHead>
              <TableHead className="text-center">Credit Pool / Status</TableHead>
              <TableHead className="text-center w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.length > 0 ? (
              data.map((item, index) => {
                const partnerUrl = `${WEB_BASE_URL}/?marketplace=${item.slug}`;
                const localItemUrl = `http://localhost:8080/?marketplace=${item.slug}`;
                const isCopied = copiedId === item.id;

                return (
                  <TableRow key={item.id || index} className="hover:bg-muted/30">
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-foreground">{item.name || "-"}</div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-amber-500/10 text-amber-700 border border-amber-300">
                        ?marketplace={item.slug}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-2.5 py-1 rounded-md text-foreground font-mono select-all">
                          {partnerUrl}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(item.slug, item.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
                          title="Copy Link for Partner"
                        >
                          {isCopied ? (
                            <>
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                              <span className="text-emerald-600 font-semibold">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>Copy URL</span>
                            </>
                          )}
                        </button>
                        {/* <button
                          type="button"
                          onClick={() => window.open(localItemUrl, "_blank")}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                          title="Open and test on localhost:8080"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Test</span>
                        </button> */}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.hasFreeCredit !== false ? (
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <Gift className="h-3.5 w-3.5" />
                            Prepaid Pool
                          </span>
                          <span className="text-[11px] font-mono text-muted-foreground">
                            {(item.usedCredits || 0)} / {(item.totalCredits != null ? item.totalCredits : (item.credits != null ? item.credits : 50))} Claimed
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-700 border border-amber-300">
                          Customer Pays
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 justify-center">
                        <CommonButton
                          variant="outline"
                          className="size-8 p-0"
                          onClick={() => handleEdit(item)}
                          title="Edit Marketplace"
                        >
                          <SquarePen className="size-4" />
                        </CommonButton>
                        <CommonButton
                          variant="outline"
                          className="size-8 p-0"
                          onClick={() => confirmDelete(item.id)}
                          title="Delete Marketplace"
                        >
                          <Trash2 className="size-4 text-red-500" />
                        </CommonButton>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="h-44">
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No marketplaces found. Click "+ Add Marketplace" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit Modal */}
      <CommonModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
        }}
        title={isEditing ? "Edit Marketplace" : "Add Marketplace"}
      >
        <div className="grid gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">
              Marketplace Name
            </label>
            <CommonTextField
              type="text"
              placeholder="e.g. Great British Watches"
              value={formData.name}
              onChange={handleNameChange}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-muted-foreground block">
                URL Parameter (slug)
              </label>
              <span className="text-[11px] text-muted-foreground/80 italic">
                Auto-generated
              </span>
            </div>
            <CommonTextField
              type="text"
              placeholder="great_british_watches"
              value={formData.slug}
              disabled
              className="bg-muted/60 cursor-not-allowed font-mono text-sm text-foreground select-all"
            />
          </div>

          {/* Live URL Preview */}
          <div className="rounded-xl border border-border/80 bg-muted/40 p-3 text-left">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase block mb-1">
              Generated Partner URL
            </span>
            <code className="text-xs font-mono text-primary font-medium break-all select-all block">
              {WEB_BASE_URL}/?marketplace={formData.slug || "partner_slug"}
            </code>
            <p className="text-[11px] text-muted-foreground mt-1">
              Send this exact URL to the partner developer to place on their button.
            </p>
          </div>

          {/* 1 Free Credit Checkbox Option */}
          <div className="rounded-xl border border-amber-300/80 bg-amber-50/60 p-3.5 flex items-start gap-3">
            <input
              type="checkbox"
              id="hasFreeCredit"
              checked={formData.hasFreeCredit}
              onChange={(e) =>
                setFormData({ ...formData, hasFreeCredit: e.target.checked })
              }
              className="mt-1 size-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
            />
            <label htmlFor="hasFreeCredit" className="cursor-pointer text-left select-none">
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Gift className="h-4 w-4 text-amber-600 inline" />
                Prepaid Credit Pool (Complimentary for Visitors)
              </span>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                When enabled, the partner covers authentication costs. Each first-time visitor receives 1 credit until the purchased pool is fully claimed.
              </p>
            </label>
          </div>

          {/* Total Purchased Credits Input */}
          {formData.hasFreeCredit && (
            <div className="rounded-xl border border-border/80 bg-card p-3.5 text-left grid gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-foreground block">
                  Total Credits Purchased by Partner
                </label>
                <span className="text-[11px] font-mono text-primary font-semibold">
                  {formData.totalCredits || 0} Credits
                </span>
              </div>
              <CommonTextField
                type="number"
                min="0"
                placeholder="e.g. 50"
                value={formData.totalCredits}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.select();
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({
                    ...formData,
                    totalCredits: val === "" ? "" : Math.max(0, parseInt(val, 10) || 0),
                  });
                }}
              />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Each visitor from this partner will receive 1 free credit until this total pool is exhausted. Once 0 credits remain, visitors will be prompted to purchase authentication on the pricing page.
              </p>
            </div>
          )}

          {/* Modal Actions */}
          <div className="flex justify-end gap-2 mt-3">
            <CommonButton
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setIsEditing(false);
                setEditId(null);
              }}
            >
              Cancel
            </CommonButton>
            <CommonButton onClick={handleSave} disabled={loading}>
              {loading ? (
                <span>{isEditing ? "Updating..." : "Saving..."}</span>
              ) : isEditing ? (
                "Update Marketplace"
              ) : (
                "Save & Generate URL"
              )}
            </CommonButton>
          </div>
        </div>
      </CommonModal>

      {/* Delete Dialog */}
      <Delete
        isOpen={showDeleteDialog}
        setIsOpen={setShowDeleteDialog}
        handleDelete={handleDelete}
      />
    </Card>
  );
};

export default Marketplace;
